import Link from 'next/link';
import { useRef } from 'react';
import Button from '@components/Button/Button';
import Heading from '@components/Heading/Heading';
import styles from '@features/cms/components/inspirations/InspirationVoxboxBlock/InspirationVoxboxBlock.styles';
import { useResponsive } from '@features/responsive';
import useIntersect from '@hooks/useIntersect';
import { IVoxboxInfoBlock } from '@features/cms/typescript';
import VideoLaptopComponent from '@components/VideoLaptopComponent/VideoLaptopComponent';
import scrollToElementById from '@utils/scrollToElementById';
import { TButtonVariant } from '@components/Button/typescript';
import theme from '@styles/theme';
import sanitizeHtml from '@utils/sanitizeHtml';
type PropsType = {
  block: IVoxboxInfoBlock;
  isFirst?: boolean;
};
const InspirationVoxboxBlock = ({
  block,
  isFirst = false
}: PropsType) => {
  const {
    title,
    subtitle,
    video,
    image,
    buttonText,
    buttonUrl,
    linkText,
    linkUrl,
    description,
    background,
    marginTop,
    marginBottom,
    textColor,
    code,
    variant
  } = block;
  const isNormalVariant = variant === 'normal';
  const sectionRef = useRef<HTMLElement>(null);
  const {
    isWiderThanDesktop
  } = useResponsive();
  const shouldRenderButton = buttonText && buttonUrl;
  const shouldRenderLink = linkUrl && linkText;
  const entry = useIntersect(sectionRef, {
    rootMargin: '-50px',
    threshold: 0.5
  });
  const renderButtons = (url: string, text: string, buttonVariant?: TButtonVariant) => url.slice(0, 1) === '#' ? <Button onClick={() => scrollToElementById(url.substring(1))} variant={buttonVariant ?? 'outlined'} size="small" variantColor="dark" cy={`${code}-button`} data-testid={`${code}-button`}>
        {text}
      </Button> : <Link href={url || '/pages/voxbox'} data-testid={`${code}-button-link`}>
        <Button variant={buttonVariant ?? 'outlined'} size="small" variantColor="dark" cy={`${code}-button`}>
          {text}
        </Button>
      </Link>;
  return <section id={code} ref={sectionRef} sx={styles.inspirationVoxboxSection(background || (isNormalVariant ? theme.rawColors.beige500 : '#506C5E1A'), marginTop ?? 0, marginBottom ?? 0)} data-testid={code}>
      <div sx={styles.leftContent}>
        {!isWiderThanDesktop && <>
            <Heading h={isFirst ? 1 : 2} display={variant === 'inspiration' ? 2 : 3} mobile pb={16} color={textColor} dangerouslySetInnerHTML={sanitizeHtml(title)} />
            {subtitle && <Heading h={4} addSx={styles.subtitle(textColor)} dangerouslySetInnerHTML={sanitizeHtml(subtitle)} />}
          </>}
        {(video?.video || image) && <div sx={styles.videoWrapper} data-testid="InspirationVoxboxBlock-videoWrapper">
            <VideoLaptopComponent video={video} image={image} playVideo={entry?.isIntersecting ?? false} title={title} />
          </div>}
      </div>
      <div sx={styles.rightContent}>
        {isWiderThanDesktop && <>
            <Heading h={isFirst ? 1 : 2} display={variant === 'inspiration' ? 2 : 3} mobile pb={16} color={textColor} dangerouslySetInnerHTML={sanitizeHtml(title)} />
            {subtitle && <Heading h={4} addSx={styles.subtitle(textColor)} dangerouslySetInnerHTML={sanitizeHtml(subtitle)} />}
          </>}
        <div sx={styles.description(textColor)}>{description}</div>
        {shouldRenderButton && renderButtons(buttonUrl, buttonText)}
        {shouldRenderLink && <div sx={styles.linkButton(shouldRenderButton ? 24 : 0)}>
            {renderButtons(linkUrl, linkText, 'link_underline')}
          </div>}
      </div>
    </section>;
};
export default InspirationVoxboxBlock;